import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  Button,
  CheckBox,
  Input,
  TopProgress,
} from "../../../../components";
import {
  Section,
} from "../styles";
import { useUi } from "../../../../context/ui.context";
import { settingsService } from "../../../../api";
import styled from "styled-components";

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 20px;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.grey[300]};
  border-radius: 10px;
  background: ${({ theme }) => theme.base.white};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
`;

const RowTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.palette[750]};
`;

const Description = styled.div`
  color: ${({ theme }) => theme.grey[600]};
  font-size: 13px;
  line-height: 18px;
`;

type AdvancedSettingsPayload = {
  disable_periodic_tasks: boolean;
  grant_all_permissions_implicitly: boolean;
  delete_activity_log_entries: boolean;
  delete_activity_log_entries_older_than_months: number;
  delete_email_log_entries: boolean;
  delete_email_log_entries_older_than_months: number;
  delete_periodic_task_log_entries: boolean;
  delete_periodic_task_log_entries_older_than_months: number;
  delete_container_logs: boolean;
  delete_container_logs_older_than_months: number;
  short_operation_step_timeout: number;
  long_operation_step_timeout: number;
};

const defaultValues: AdvancedSettingsPayload = {
  disable_periodic_tasks: false,
  grant_all_permissions_implicitly: true,
  delete_activity_log_entries: false,
  delete_activity_log_entries_older_than_months: 24,
  delete_email_log_entries: false,
  delete_email_log_entries_older_than_months: 24,
  delete_periodic_task_log_entries: false,
  delete_periodic_task_log_entries_older_than_months: 24,
  delete_container_logs: true,
  delete_container_logs_older_than_months: 24,
  short_operation_step_timeout: 5,
  long_operation_step_timeout: 60,
};

const AdvancedSettings: FC = () => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [initialValues, setInitialValues] =
    useState<AdvancedSettingsPayload>(defaultValues);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        disable_periodic_tasks: yup.bool(),
        grant_all_permissions_implicitly: yup.bool(),
        delete_activity_log_entries: yup.bool(),
        delete_activity_log_entries_older_than_months: yup
          .number()
          .min(1, t("MINIMUM_VALUE"))
          .required(t("REQUIRED_FIELD")),
        delete_email_log_entries: yup.bool(),
        delete_email_log_entries_older_than_months: yup
          .number()
          .min(1, t("MINIMUM_VALUE"))
          .required(t("REQUIRED_FIELD")),
        delete_periodic_task_log_entries: yup.bool(),
        delete_periodic_task_log_entries_older_than_months: yup
          .number()
          .min(1, t("MINIMUM_VALUE"))
          .required(t("REQUIRED_FIELD")),
        delete_container_logs: yup.bool(),
        delete_container_logs_older_than_months: yup
          .number()
          .min(1, t("MINIMUM_VALUE"))
          .required(t("REQUIRED_FIELD")),
        short_operation_step_timeout: yup
          .number()
          .min(1, t("MINIMUM_VALUE"))
          .required(t("REQUIRED_FIELD")),
        long_operation_step_timeout: yup
          .number()
          .min(1, t("MINIMUM_VALUE"))
          .required(t("REQUIRED_FIELD")),
      }),
    [t]
  );

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    handleSubmit,
    isValid,
  } = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues,
    onSubmit: async (formValues: AdvancedSettingsPayload) => {
      setSaving(true);
      try {
        await settingsService.saveAdvancedSettings(formValues);
        viewAlert({
          severity: "success",
          message: t("SETTINGS_SAVED_SUCCESSFULLY"),
        });
      } catch (e: any) {
        viewAlert({
          severity: "error",
          message:
            e?.response?.data?.detail ||
            e?.response?.data?.error ||
            t("ERROR_OCCURRED"),
        });
      } finally {
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    const loadData = async () => {
      setPageLoading(true);
      try {
        const res = await settingsService.getAdvancedSettings();
        const data =
          res?.data?.advanced_settings || res?.data || defaultValues;
        setInitialValues({ ...defaultValues, ...data });
      } catch (e: any) {
        viewAlert({
          severity: "error",
          message:
            e?.response?.data?.detail ||
            e?.response?.data?.error ||
            t("FAILED_TO_LOAD_DATA"),
        });
      } finally {
        setPageLoading(false);
      }
    };

    loadData();
  }, [t, viewAlert]);

  if (pageLoading) {
    return <TopProgress loading={pageLoading} />;
  }

  return (
    <Section>
      <form onSubmit={handleSubmit}>
        <FormGrid>
          <FormRow>
            <RowTitle>{t("PERIODIC_TASKS")}</RowTitle>
            <CheckBox
              title={t("DISABLE_PERIODIC_TASKS")}
              value={values.disable_periodic_tasks}
              onChange={() =>
                setFieldValue(
                  "disable_periodic_tasks",
                  !values.disable_periodic_tasks
                )
              }
            />
            <Description>
              {t(
                "DISABLE_PERIODIC_TASKS_DESCRIPTION",
                "Checking this will prevent periodic tasks from running."
              )}
            </Description>
          </FormRow>

          <FormRow>
            <RowTitle>{t("PERMISSIONS")}</RowTitle>
            <CheckBox
              title={t("GRANT_ALL_PERMISSIONS_IMPLICITLY")}
              value={values.grant_all_permissions_implicitly}
              onChange={() =>
                setFieldValue(
                  "grant_all_permissions_implicitly",
                  !values.grant_all_permissions_implicitly
                )
              }
            />
            <Description>
              {t(
                "GRANT_ALL_PERMISSIONS_IMPLICITLY_HELP",
                "Grants all permissions to users that have no specific permissions."
              )}
            </Description>
          </FormRow>

          <FormRow>
            <RowTitle>{t("ACTIVITY_LOGS")}</RowTitle>
            <CheckBox
              title={t("DELETE_ACTIVITY_LOG_ENTRIES")}
              value={values.delete_activity_log_entries}
              onChange={() =>
                setFieldValue(
                  "delete_activity_log_entries",
                  !values.delete_activity_log_entries
                )
              }
            />
            <Input
              title={t("DELETE_ACTIVITY_LOG_ENTRIES_OLDER_THAN")}
              placeholder={t("MONTHS")}
              fieldName="delete_activity_log_entries_older_than_months"
              handleChange={handleChange}
              handleBlur={handleBlur}
              values={values}
              errors={errors}
              touched={touched}
              type="number"
              disabled={!values.delete_activity_log_entries}
            />
            <Description>
              {t(
                "DELETE_ACTIVITY_LOG_ENTRIES_DESCRIPTION",
                "Number of months of activity logs to keep."
              )}
            </Description>
          </FormRow>

          <FormRow>
            <RowTitle>{t("EMAIL_LOGS")}</RowTitle>
            <CheckBox
              title={t("DELETE_EMAIL_LOG_ENTRIES")}
              value={values.delete_email_log_entries}
              onChange={() =>
                setFieldValue(
                  "delete_email_log_entries",
                  !values.delete_email_log_entries
                )
              }
            />
            <Input
              title={t("DELETE_EMAIL_LOG_ENTRIES_OLDER_THAN")}
              placeholder={t("MONTHS")}
              fieldName="delete_email_log_entries_older_than_months"
              handleChange={handleChange}
              handleBlur={handleBlur}
              values={values}
              errors={errors}
              touched={touched}
              type="number"
              disabled={!values.delete_email_log_entries}
            />
            <Description>
              {t(
                "DELETE_EMAIL_LOG_ENTRIES_DESCRIPTION",
                "Number of months of email logs to keep."
              )}
            </Description>
          </FormRow>

          <FormRow>
            <RowTitle>{t("PERIODIC_TASK_LOGS")}</RowTitle>
            <CheckBox
              title={t("DELETE_PERIODIC_TASK_LOG_ENTRIES")}
              value={values.delete_periodic_task_log_entries}
              onChange={() =>
                setFieldValue(
                  "delete_periodic_task_log_entries",
                  !values.delete_periodic_task_log_entries
                )
              }
            />
            <Input
              title={t("DELETE_PERIODIC_TASK_LOGS_OLDER_THAN")}
              placeholder={t("MONTHS")}
              fieldName="delete_periodic_task_log_entries_older_than_months"
              handleChange={handleChange}
              handleBlur={handleBlur}
              values={values}
              errors={errors}
              touched={touched}
              type="number"
              disabled={!values.delete_periodic_task_log_entries}
            />
            <Description>
              {t(
                "DELETE_PERIODIC_TASK_LOGS_DESCRIPTION",
                "Number of months of periodic task logs to keep."
              )}
            </Description>
          </FormRow>

          <FormRow>
            <RowTitle>{t("CONTAINER_LOGS")}</RowTitle>
            <CheckBox
              title={t("DELETE_CONTAINER_LOGS")}
              value={values.delete_container_logs}
              onChange={() =>
                setFieldValue(
                  "delete_container_logs",
                  !values.delete_container_logs
                )
              }
            />
            <Input
              title={t("DELETE_CONTAINER_LOGS_OLDER_THAN")}
              placeholder={t("MONTHS")}
              fieldName="delete_container_logs_older_than_months"
              handleChange={handleChange}
              handleBlur={handleBlur}
              values={values}
              errors={errors}
              touched={touched}
              type="number"
              disabled={!values.delete_container_logs}
            />
            <Description>
              {t(
                "DELETE_CONTAINER_LOGS_DESCRIPTION",
                "Number of months of container logs to keep."
              )}
            </Description>
          </FormRow>

          <FormRow>
            <RowTitle>{t("OPERATIONS_TIMEOUTS")}</RowTitle>
            <Input
              title={t("SHORT_OPERATION_STEP_TIMEOUT_MIN")}
              placeholder={t("MINUTES")}
              fieldName="short_operation_step_timeout"
              handleChange={handleChange}
              handleBlur={handleBlur}
              values={values}
              errors={errors}
              touched={touched}
              type="number"
            />
            <Input
              title={t("LONG_OPERATION_STEP_TIMEOUT_MIN")}
              placeholder={t("MINUTES")}
              fieldName="long_operation_step_timeout"
              handleChange={handleChange}
              handleBlur={handleBlur}
              values={values}
              errors={errors}
              touched={touched}
              type="number"
            />
            <Description>
              {t(
                "OPERATIONS_TIMEOUTS_DESCRIPTION",
                "Adjust when steps waiting on external actions should time out."
              )}
            </Description>
          </FormRow>
        </FormGrid>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
          <Button
            title={t("SAVE_SETTINGS")}
            onClick={() => handleSubmit()}
            disabled={!isValid || saving}
            loading={saving}
          />
        </div>
      </form>
    </Section>
  );
};

export default AdvancedSettings;
